import React, { useState, useEffect, useRef } from "react";
import { X, Send, Award, RefreshCw } from "lucide-react";

export default function SocraticExaminerModal({ note, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionCompleted, setSessionCompleted] = useState(false);
  const [rubricScores, setRubricScores] = useState(null);
  const chatEndRef = useRef(null);

  const cleanNoteText = (note?.html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  useEffect(() => {
    // Initialize examiner dialogue
    const title = note?.title || "Research Topic";
    const initialQuestion = cleanNoteText.length > 50
      ? `Welcome to your oral defence on "${title}". In your notes, you put forward several key arguments. To begin: What is the primary empirical justification supporting your central thesis, and what foundational assumption would render your argument invalid if disproven?`
      : `Welcome to your oral defence on "${title}". State your core hypothesis in two sentences, along with the primary academic literature that grounds your position.`;

    setMessages([
      {
        sender: "examiner",
        text: initialQuestion,
        timestamp: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      },
    ]);
  }, [note, cleanNoteText]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userText = input.trim();
    const userMsg = {
      sender: "student",
      text: userText,
      timestamp: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    };

    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    const turnCount = messages.filter((m) => m.sender === "student").length + 1;

    setTimeout(() => {
      if (turnCount >= 3) {
        // Conclude session and provide rubric analysis
        const finalResponse = `Thank you. That concludes this oral examination cycle. Your defensive responses have been synthesized and evaluated across standard academic viva voce criteria.`;
        setMessages((prev) => [
          ...prev,
          {
            sender: "examiner",
            text: finalResponse,
            timestamp: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
          },
        ]);
        setRubricScores({
          coherence: 86,
          evidentialRigour: 79,
          rebuttalEfficacy: 91,
          feedback: [
            "Strong articulation of central thesis under direct cross-examination.",
            "Effective concession of boundary conditions without compromising foundational claims.",
            "Recommendation: Strengthen quantitative citations and primary literature backing in paragraph 2.",
          ],
        });
        setSessionCompleted(true);
        setLoading(false);
      } else {
        // Generate challenging follow-up question
        let followUp = "";
        if (turnCount === 1) {
          followUp = `You argue that "${userText.slice(0, 70)}...". How do you address the counter-critique that this evidence demonstrates correlation rather than causation? Which specific methodological controls isolate your variables?`;
        } else if (turnCount === 2) {
          followUp = `Consider an alternative theoretical framework in this discipline that reaches the opposite conclusion. What is the strongest competing counter-argument to your position, and why is your interpretation superior?`;
        }

        setMessages((prev) => [
          ...prev,
          {
            sender: "examiner",
            text: followUp,
            timestamp: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
          },
        ]);
        setLoading(false);
      }
    }, 900);
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "color-mix(in srgb, var(--iv-band) 45%, transparent)",
        backdropFilter: "blur(4px)",
        zIndex: 1100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        style={{
          width: "min(740px, 95vw)",
          height: "min(640px, 90vh)",
          background: "var(--iv-surface)",
          borderRadius: 14,
          boxShadow: "0 16px 40px color-mix(in srgb, var(--iv-shadow) 18%, transparent)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          border: "1px solid var(--iv-line)",
        }}
      >
        {/* Modal Header */}
        <div
          style={{
            padding: "16px 20px",
            borderBottom: "1px solid var(--iv-line)",
            background: "var(--iv-bg)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: "var(--iv-band)",
                color: "var(--iv-on-band)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: 14,
              }}
            >
              S
            </div>
            <div>
              <div style={{ fontSize: 14.5, fontWeight: 700, color: "var(--iv-body)" }}>
                Socratic Examiner: Oral Defence Simulator
              </div>
              <div style={{ fontSize: 11.5, color: "var(--iv-muted)" }}>
                Topic: {note?.title || "Untitled Note"}
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              padding: 6,
              borderRadius: 6,
              color: "var(--iv-muted)",
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Chat History */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "20px 22px",
            display: "flex",
            flexDirection: "column",
            gap: 16,
            background: "var(--iv-bg)",
          }}
        >
          {messages.map((m, idx) => {
            const isExaminer = m.sender === "examiner";
            return (
              <div
                key={idx}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: isExaminer ? "flex-start" : "flex-end",
                  maxWidth: "85%",
                  alignSelf: isExaminer ? "flex-start" : "flex-end",
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: isExaminer ? "var(--iv-accent)" : "var(--iv-body)",
                    marginBottom: 4,
                    padding: "0 4px",
                  }}
                >
                  {isExaminer ? "Oral Examiner" : "Student Defence"} · {m.timestamp}
                </div>
                <div
                  style={{
                    padding: "12px 16px",
                    borderRadius: 12,
                    background: isExaminer ? "var(--iv-surface)" : "var(--iv-band)",
                    color: isExaminer ? "var(--iv-body)" : "var(--iv-on-band)",
                    border: isExaminer ? "1px solid var(--iv-line)" : "none",
                    boxShadow: "0 1px 3px color-mix(in srgb, var(--iv-shadow) 4%, transparent)",
                    fontSize: 13.5,
                    lineHeight: 1.6,
                  }}
                >
                  {m.text}
                </div>
              </div>
            );
          })}

          {loading && (
            <div
              style={{
                alignSelf: "flex-start",
                background: "var(--iv-surface)",
                border: "1px solid var(--iv-line)",
                borderRadius: 12,
                padding: "10px 16px",
                fontSize: 12.5,
                color: "var(--iv-muted)",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <RefreshCw size={14} style={{ animation: "spin 1s linear infinite" }} />
              Examiner is formulating follow-up interrogation...
            </div>
          )}

          {/* Rubric Breakdown when session completes */}
          {sessionCompleted && rubricScores && (
            <div
              style={{
                marginTop: 12,
                background: "var(--iv-surface)",
                border: "1.5px solid var(--iv-head)",
                borderRadius: 12,
                padding: "18px 20px",
                boxShadow: "0 4px 14px color-mix(in srgb, var(--iv-shadow) 6%, transparent)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                <Award size={18} color="var(--iv-head)" />
                <div style={{ fontWeight: 700, fontSize: 14.5, color: "var(--iv-head)" }}>
                  Oral Defence Rubric Evaluation
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 16 }}>
                <div style={{ background: "var(--iv-bg)", borderRadius: 8, padding: "10px 12px", textAlign: "center" }}>
                  <div style={{ fontSize: 11, color: "var(--iv-muted)", fontWeight: 600, textTransform: "uppercase" }}>
                    Coherence
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: "var(--iv-body)", marginTop: 2 }}>
                    {rubricScores.coherence}%
                  </div>
                </div>
                <div style={{ background: "var(--iv-bg)", borderRadius: 8, padding: "10px 12px", textAlign: "center" }}>
                  <div style={{ fontSize: 11, color: "var(--iv-muted)", fontWeight: 600, textTransform: "uppercase" }}>
                    Evidential Rigour
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: "var(--iv-body)", marginTop: 2 }}>
                    {rubricScores.evidentialRigour}%
                  </div>
                </div>
                <div style={{ background: "var(--iv-bg)", borderRadius: 8, padding: "10px 12px", textAlign: "center" }}>
                  <div style={{ fontSize: 11, color: "var(--iv-muted)", fontWeight: 600, textTransform: "uppercase" }}>
                    Rebuttal Efficacy
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: "var(--iv-body)", marginTop: 2 }}>
                    {rubricScores.rebuttalEfficacy}%
                  </div>
                </div>
              </div>

              <div style={{ fontSize: 12, fontWeight: 600, color: "var(--iv-body)", marginBottom: 6 }}>
                Examiner Feedback & Recommendations:
              </div>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: "var(--iv-body)", lineHeight: 1.6 }}>
                {rubricScores.feedback.map((f, i) => (
                  <li key={i}>{f}</li>
                ))}
              </ul>
            </div>
          )}

          <div ref={chatEndRef} />
        </div>

        {/* Input Bar */}
        <div
          style={{
            padding: "14px 18px",
            borderTop: "1px solid var(--iv-line)",
            background: "var(--iv-raised)",
            display: "flex",
            gap: 10,
            alignItems: "center",
          }}
        >
          <input
            type="text"
            placeholder={sessionCompleted ? "Examination concluded." : "Articulate your academic defence..."}
            value={input}
            disabled={sessionCompleted || loading}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSend();
            }}
            style={{
              flex: 1,
              padding: "10px 14px",
              borderRadius: 8,
              border: "1px solid var(--iv-line)",
              outline: "none",
              fontSize: 13.5,
              background: sessionCompleted ? "var(--iv-bg)" : "var(--iv-raised)",
            }}
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={sessionCompleted || loading || !input.trim()}
            style={{
              padding: "10px 18px",
              borderRadius: 8,
              border: "none",
              background: input.trim() && !sessionCompleted && !loading ? "var(--iv-btn)" : "var(--iv-fill)",
              color: input.trim() && !sessionCompleted && !loading ? "var(--iv-btn-fg)" : "var(--iv-muted)",
              cursor: input.trim() && !sessionCompleted && !loading ? "pointer" : "default",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontSize: 13,
              fontWeight: 600,
              transition: "all 0.15s ease",
            }}
          >
            <Send size={14} /> Send
          </button>
        </div>
      </div>
    </div>
  );
}
